import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  HStack,
  Heading,
  Box,
  Image,
  Button,
  VStack,
  Input,
  Divider,
} from '@chakra-ui/react';
import { useDebounce } from 'usehooks-ts';

import projectImage from '../../../assets/tasks/project.svg';
import formTemplateImage from '../../../assets/projects/form_template_list.svg';
import workflowImage from '../../../assets/projects/workflow_list.svg';

import {
  useGetAllProjectsQuery,
  useGetAllPhasesQuery,
  useGetAllWorkflowsQuery,
} from '../../../services';
import { ProjectType } from '../../../models';

export const NewTaskMetaDataModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: (formId?: number, projectId?: number, phaseId?: number) => void;
}) => {
  const [newTaskStep, setNewTaskStep] = useState<
    'project' | 'phase' | 'workflow'
  >('project');
  const [workflowId, setWorkflowId] = useState<number>();
  const [projectId, setProjectId] = useState<number>();
  const [phaseId, setPhaseId] = useState<number>();
  const [searchProject, setSearchProject] = useState<string>();
  const [searchPhase, setSearchPhase] = useState<string>();

  const debouncedSearchProject = useDebounce(searchProject, 300);
  const debouncedSearchPhase = useDebounce(searchPhase, 300);

  const { data: projects } = useGetAllProjectsQuery({
    page: 0,
    size: 1000,
    projectIdentifier: debouncedSearchProject,
  });

  const selectedProject = projects?.data.content.find(
    ({ id }) => id === projectId
  )?.projectTypeEnum;

  const { data: phases } = useGetAllPhasesQuery(
    {
      page: 0,
      size: 1000,
      projectId: Number(projectId),
      phaseName: debouncedSearchPhase,
    },
    {
      skip: !projectId || selectedProject === ProjectType.SINGLE,
    }
  );

  const { data: workflows } = useGetAllWorkflowsQuery(
    {
      page: 0,
      size: 1000,
      projectId: String(projectId),
    },
    {
      skip: !projectId,
    }
  );

  const handleNewTaskSteps = () => {
    const selectedProject = projects?.data.content.find(
      project => project.id === projectId
    );

    if (!selectedProject) return;

    if (newTaskStep === 'project' && projectId) {
      if (selectedProject.projectTypeEnum === ProjectType.SINGLE) {
        setNewTaskStep('workflow');
      } else {
        setNewTaskStep('phase');
      }
    } else if (newTaskStep === 'phase' && phaseId) {
      setNewTaskStep('workflow');
    } else if (newTaskStep === 'workflow' && workflowId)
      onClose(workflowId, projectId, phaseId);
  };

  return (
    <Modal
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
    >
      <ModalOverlay />

      <ModalContent pb="4">
        <ModalHeader>
          <Heading fontSize="md" fontWeight="semibold" color="custom_blue.1">
            {newTaskStep === 'workflow' && 'Select Form'}
            {newTaskStep === 'phase' && 'Select Phase'}
            {newTaskStep === 'project' && 'Select Project'}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {newTaskStep === 'project' && (
            <VStack align="stretch" maxH="64" overflowY="scroll" p="1">
              <Box>
                <Input
                  placeholder="Search"
                  onChange={e => setSearchProject(e.target.value)}
                />
              </Box>

              {projects?.data.content.map(({ id, projectName }) => (
                <HStack
                  key={id}
                  border="1px"
                  borderColor={
                    id === projectId ? 'custom_orange.2' : 'custom_gray.4'
                  }
                  borderRadius="md"
                  p="2"
                  onClick={() => setProjectId(id)}
                  cursor="pointer"
                >
                  <HStack flex="1">
                    <Image src={projectImage} />
                    <Box fontWeight="medium" fontSize="md">
                      {projectName}
                    </Box>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}
          {newTaskStep === 'phase' && (
            <VStack align="stretch" maxH="64" overflowY="scroll">
              <Input
                placeholder="Search"
                onChange={e => setSearchPhase(e.target.value)}
              />

              {phases?.data.content.map(({ id, phaseName }) => (
                <HStack
                  key={id}
                  border="1px"
                  borderColor={
                    id === phaseId ? 'custom_orange.2' : 'custom_gray.4'
                  }
                  borderRadius="md"
                  p="2"
                  onClick={() => setPhaseId(id)}
                  cursor="pointer"
                >
                  <HStack flex="1">
                    <Image src={projectImage} />
                    <Box fontWeight="medium" fontSize="md">
                      {phaseName}
                    </Box>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}

          {newTaskStep === 'workflow' && (
            <VStack align="stretch" maxH="64" overflowY="scroll">
              {workflows?.data.content.map(({ id, name, formName }) => (
                <HStack
                  key={id}
                  border="1px"
                  borderColor={
                    id === workflowId ? 'custom_orange.2' : 'custom_gray.4'
                  }
                  borderRadius="md"
                  p="2"
                  onClick={() => {
                    setWorkflowId(id);
                  }}
                  cursor="pointer"
                >
                  <HStack flex="1">
                    <Image src={workflowImage} />
                    <Box fontWeight="medium" fontSize="md">
                      {name}
                    </Box>
                  </HStack>

                  <Divider orientation="vertical" h="8" />

                  <HStack flex="1">
                    <Image src={formTemplateImage} />
                    <Box fontWeight="medium" fontSize="md">
                      {formName}
                    </Box>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}

          <Button
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            onClick={handleNewTaskSteps}
            w="full"
            mt="6"
            isDisabled={
              (newTaskStep === 'project' && !projectId) ||
              (newTaskStep === 'phase' && !phaseId) ||
              (newTaskStep === 'workflow' && !workflowId)
            }
          >
            Next
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
